import { useState, useEffect } from 'preact/hooks'
import { getDocuments } from '@/services/api'
import { getProcessId } from '@/services/session'
import type { DocumentDto } from '@cencert/mob-signature'
import { useTranslation } from 'react-i18next'

interface UseGetDocumentsReturn {
  documents: DocumentDto[]
  isLoading: boolean
  isInitialLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useGetDocuments({
  fetchOnMount,
}: {
  fetchOnMount?: boolean
} = {}): UseGetDocumentsReturn {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(
    fetchOnMount ?? true
  )
  const [error, setError] = useState<string | null>(null)

  const fetchDocuments = async (isInitial = false) => {
    const processId = getProcessId()

    if (!processId) {
      setError(t('documentAdd.errors.noIdProcess'))
      setIsInitialLoading(false)
      return
    }

    setIsLoading(true)
    if (isInitial) {
      setIsInitialLoading(true)
    }
    setError(null)

    await getDocuments(processId, {
      onSuccess: response => {
        setDocuments(response ?? [])
        setIsLoading(false)
        if (isInitial) {
          setIsInitialLoading(false)
        }
      },
      onError: err => {
        const errorMessage =
          err.response?.data?.detail ??
          err.message ??
          t('documentAdd.errors.downloadingDocumentsError')
        setError(errorMessage)
        setIsLoading(false)
        if (isInitial) {
          setIsInitialLoading(false)
        }
      },
    })
  }

  const refetch = async () => {
    await fetchDocuments(false)
  }

  useEffect(() => {
    if (fetchOnMount) {
      fetchDocuments(true)
    }
  }, [fetchOnMount])

  return {
    documents,
    isLoading,
    isInitialLoading,
    error,
    refetch,
  }
}
